import {EVENT_TYPE_CALL, EVENT_TYPE_CHAT, EVENT_TYPE_EMAIL, EVENT_TYPE_FEEDBACK} from 'utils/eventConstants';
import i18n from 'i18n';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import {isFieldRestricted, getRestrictedFieldPlaceholder} from '@/utils/fieldRestrictions';
interface ContactData {
    person?: {
        firstName?: string;
        lastName?: string;
    };
    value?: string;
}

interface EventActor {
    agentId?: number;
    customerId?: number;
    agent?: {
        fullName?: string;
    };
    customer?: {
        firstName?: string;
        lastName?: string;
        externalId?: string;
        optIn?: boolean;
        optInDate?: string;
    };
    actorType?: string;
    type?: string;
    contactData?: ContactData;
    first_name?: string;
    last_name?: string;
    external_id?: string;
}

interface Event {
    eventActors?: EventActor[];
    eventType?: {
        name: string;
    };
}

interface SearchEvent {
    event_actors?: EventActor[];
    event_type_name?: string;
    agent_first_name?: string;
    agent_last_name?: string;
    agent_id?: number;
    customer_first_name?: string;
    customer_last_name?: string;
    customer_external_id?: string;
    customer_id?: number;
}

interface UserData {
    permissions?: string[];
    indexAnonymized?: boolean;
}

interface EventAgent {
    agentName: string;
    id?: number | null;
    value?: string | null;
    type?: string;
}

interface EventCustomer {
    customerName: string;
    id?: number | string | null;
    value?: string | null;
    optIn?: boolean | null;
    optInDate?: string | null;
}

interface EventActorsResult {
    eventAgent: EventAgent | EventAgent[] | string | null;
    eventCustomer: EventCustomer | null;
}

export const EventActorsList = (event: Event | null, searchEvent: SearchEvent | null, userData: UserData | null, restrictedFields?: string[] | null): EventActorsResult => {
    const dataSource = event ? 'event' : 'searchEvent';
    let eventActors: EventActor[] = [];
    
    if (dataSource === 'event') {
        eventActors = event?.eventActors || [];
    } else {
        eventActors = searchEvent?.event_actors || [];
    }

    const eventType = event?.eventType ? event?.eventType.name : searchEvent?.event_type_name;
    const userHasPermission = hasPermission('fs.event.index_data.read', userData?.permissions, true) && !userData?.indexAnonymized;

    const agentNameRestricted = isFieldRestricted('agent_first_name', restrictedFields);
    const customerNameRestricted = isFieldRestricted('customer_first_name', restrictedFields);
    const restrictedPlaceholder = getRestrictedFieldPlaceholder();

    let eventAgent: EventAgent | EventAgent[] | string | null = null;
    let eventCustomer: EventCustomer | null = null;

    if (eventType === EVENT_TYPE_CALL) {
        let eventAgentName: string;
        let eventAgentId: number | null;
        eventAgent = (() => {
            if (agentNameRestricted) {
                return {agentName: restrictedPlaceholder, id: null};
            }
            if (dataSource === 'event') {
                eventAgentName = ((event?.eventActors?.find((c) => c.agent != null)?.agent?.fullName || '')).trim();
                eventAgentId = ((event?.eventActors?.find((c) => c.agent != null)?.agentId || null));
            } else {
                eventAgentName = ((searchEvent?.agent_first_name || '') + ' ' + (searchEvent?.agent_last_name || '')).trim();
                eventAgentId = searchEvent?.agent_id || null;
            }
            if (!userHasPermission || !eventAgentName) {
                return i18n.t('jt_no_data_available.text');
            }
            return {agentName: eventAgentName, id: eventAgentId};
        })();

        eventCustomer = (() => {
            if (customerNameRestricted) {
                return {customerName: restrictedPlaceholder};
            }
            let customerName: string;
            let eventCustomerId: number | null;
            let eventCustomerOptInStatus: boolean | null;
            let eventCustomerOptInDate: string | null;

            if (dataSource === 'event') {
                const customer = (event?.eventActors?.find((c) => c.customer != null));
                customerName = ((customer?.customer?.firstName || '') + ' ' + (customer?.customer?.lastName || '') + ' (' + (customer?.customer?.externalId || '') + ')').trim();
                eventCustomerId = ((event?.eventActors?.find((c) => c.customer != null)?.customerId || null));
                eventCustomerOptInStatus = searchEvent?.customer_id ? event?.eventActors?.find((c) => c.customer != null)?.customer?.optIn || null : null;
                eventCustomerOptInDate = event?.eventActors?.find((c) => c.customer != null)?.customer?.optInDate || null;
            } else {
                customerName = ((searchEvent?.customer_first_name || '') + ' ' + (searchEvent?.customer_last_name || '') + ' ' + (searchEvent?.customer_external_id || '')).trim();
                eventCustomerId = searchEvent?.customer_id || null;
                eventCustomerOptInStatus = null;
                eventCustomerOptInDate = null;
            }

            if (!userHasPermission) {
                return {customerName: i18n.t('jt_no_data_available.text')};
            }

            return {
                customerName: customerName,
                id: eventCustomerId,
                optIn: eventCustomerOptInStatus,
                optInDate: eventCustomerOptInDate
            };
        })();

    } else if (eventType === EVENT_TYPE_CHAT) {
        eventAgent = (() => {
            if (agentNameRestricted) {
                return {agentName: restrictedPlaceholder, value: null};
            }
            if (dataSource === 'event') {
                const responder = eventActors.find(eventActor => eventActor.actorType === 'responder');
                if (responder) {
                    let agentName: string;
                    if (responder?.contactData?.person?.firstName || responder?.contactData?.person?.lastName) {
                        agentName = ((responder?.contactData?.person?.firstName || '') + ' ' + (responder?.contactData?.person?.lastName || '')).trim();
                    } else {
                        agentName = '';
                    }
                    return userHasPermission ? {
                        agentName: agentName,
                        value: null
                    } : {agentName: i18n.t('jt_no_data_available.text'), value: null};
                }
            } else {
                const responder = eventActors.find(eventActor => eventActor.type === 'responder');
                if (responder) {
                    let agentName: string;
                    agentName = ((responder?.first_name || '') + ' ' + (responder?.last_name || responder?.external_id || '')).trim();
                    return userHasPermission ? {
                        agentName: agentName,
                        value: null
                    } : {agentName: i18n.t('jt_no_data_available.text'), value: null};
                }
                return {agentName: '', value: null};
            }
            return null;
        })();

        eventCustomer = (() => {
            if (customerNameRestricted) {
                return {customerName: restrictedPlaceholder, value: null};
            }
            if (dataSource === 'event') {
                const responder = eventActors.find(eventActor => eventActor.actorType === 'initiator');
                if (responder && responder.contactData) {
                    const {person, value} = responder.contactData;
                    let customerName: string;
                    if (person?.firstName || person?.lastName) {
                        customerName = `${person.firstName || ''} ${person.lastName || ''}`.trim();
                    } else {
                        customerName = value || '';
                    }
                    return userHasPermission ? {
                        customerName: customerName,
                        value: value || null
                    } : {customerName: i18n.t('jt_no_data_available.text'), value: null};
                }
            } else if (dataSource === 'searchEvent') {
                const responder = eventActors.find(eventActor => eventActor.type === 'initiator');
                if (responder) {
                    let customerName: string;
                    customerName = ((responder?.first_name || '') + ' ' + (responder?.last_name || responder?.external_id || '')).trim();
                    return userHasPermission ? {
                        customerName: customerName,
                        value: null
                    } : {customerName: i18n.t('jt_no_data_available.text'), value: null};
                }
                return {customerName: '', value: null};
            }
            return null;
        })();
    } else if (eventType === EVENT_TYPE_EMAIL) {
        eventCustomer = (() => {
            if (customerNameRestricted) {
                return {customerName: restrictedPlaceholder, value: null};
            }
            if (dataSource === 'event') {
                const sender = eventActors.find(eventActor => eventActor.actorType === 'sender');
                if (sender && sender.contactData) {
                    const {person, value} = sender.contactData;
                    let customerName: string;
                    if (person?.firstName || person?.lastName) {
                        customerName = `${person.firstName || ''} ${person.lastName || ''}`.trim();
                    } else {
                        customerName = value || '';
                    }
                    return userHasPermission ? {
                        customerName: customerName,
                        value: null
                    } : {customerName: i18n.t('jt_no_data_available.text'), value: null};
                }
            } else {
                const responder = eventActors.find(eventActor => eventActor.type === 'sender');
                if (responder) {
                    let customerName: string;
                    customerName = ((responder?.first_name || '') + ' ' + (responder?.last_name || responder?.external_id || '')).trim();
                    return userHasPermission ? {
                        customerName: customerName,
                        value: null
                    } : {customerName: i18n.t('jt_no_data_available.text'), value: null};
                }
                return {customerName: '', value: null};
            }
            return null;
        })();

        eventAgent = (() => {
            if (agentNameRestricted) {
                return [{type: '', agentName: restrictedPlaceholder, value: null}];
            }
            const agents = eventActors
                .filter(eventActor => eventActor.actorType !== 'sender')
                .map(eventActor => {
                    const type = eventActor.actorType === i18n.t('jt_recipient.text') ? i18n.t('jt_to.text') : eventActor.actorType;

                    if (eventActor.contactData) {
                        const {person, value} = eventActor.contactData;
                        let agentName: string;
                        if (person?.firstName || person?.lastName) {
                            agentName = `${person.firstName || ''} ${person.lastName || ''}`.trim();
                        } else {
                            agentName = value || '';
                        }
                        return userHasPermission ? {type, agentName, value: value || null} : {
                            type,
                            agentName: i18n.t('jt_no_data_available.text'),
                            value: null
                        };
                    }
                    return null;
                })
                .filter(Boolean) as EventAgent[];

            return agents.length > 0 ? agents : null;
        })();
    } else if (eventType === EVENT_TYPE_FEEDBACK) {
        eventAgent = {agentName: '', value: null};
        eventCustomer = (() => {
            if (customerNameRestricted) {
                return {customerName: restrictedPlaceholder, value: null};
            }
            if (dataSource === 'event') {
                const responder = eventActors.find(eventActor => eventActor.actorType === 'responder');
                if (responder && responder.contactData) {
                    const {person, value} = responder.contactData;
                    let customerName: string;
                    if (person?.firstName || person?.lastName) {
                        customerName = `${person.firstName || ''} ${person.lastName || ''}`.trim();
                    } else {
                        customerName = value || '';
                    }
                    return userHasPermission ? {customerName, value: value || null} : {
                        customerName: i18n.t('jt_no_data_available.text'),
                        value: null
                    };
                }
                return {customerName: '', value: null};
            } else {
                const responder = eventActors.find(eventActor => eventActor.type === 'responder');
                if (responder) {
                    let customerName: string;
                    customerName = ((responder?.first_name || '') + ' ' + (responder?.last_name || responder?.external_id || '')).trim();
                    return userHasPermission ? {
                        customerName: customerName,
                        value: null
                    } : {customerName: i18n.t('jt_no_data_available.text'), value: null};
                }
                return {customerName: '', value: null};
            }
        })();
    }

    return {eventAgent, eventCustomer};
};
